import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { database, storage } from './firebase';
import { ref, push, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { PET_TYPES, PET_SIZES } from './constants';

const RescuerForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    // Rescuer data
    rescuerName: '',
    rescuerPhone: '',
    rescuerEmail: '',
    // Pet data
    name: '',
    type: '',
    color: '',
    size: '',
    sex: '',
    age: '',
    status: 'healthy',
    description: '',
    notes: '',
    location: '',
    images: [],
    termsAccepted: false,
    hp_field: '' // Honeypot (invisible para humanos)
  });

  const [startTime] = useState(Date.now()); // Para detectar envíos ultra-rápidos de bots
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Honeypot check: Si este campo tiene algo, es un bot
    if (formData.hp_field) {
      return; 
    }

    // 2. Speed check: Si se envía en menos de 3 segundos, probablemente sea un bot
    const timeTaken = (Date.now() - startTime) / 1000;
    if (timeTaken < 3) {
      setError("Por favor, tómate un momento para revisar los datos antes de enviar.");
      return;
    }
    
    if (!formData.termsAccepted) {
      alert("Debe aceptar los términos y condiciones");
      return;
    }

    if (imagePreviews.length === 0) {
      alert("Debe agregar al menos una imagen de la mascota");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const newPetRef = push(ref(database, 'pets'));
      const petId = newPetRef.key;

      // Upload images
      const uploadImage = async (image, index) => {
        const imageName = `${formData.name.replace(/\s+/g, '-')}-${petId}-${Date.now()}-${index}`;
        const imageRef = storageRef(storage, `pets/${imageName}`);
        const response = await fetch(image);
        const blob = await response.blob();
        await uploadBytes(imageRef, blob);
        return await getDownloadURL(imageRef);
      };

      const imageUrls = await Promise.all(
        imagePreviews.map((preview, index) => uploadImage(preview, index))
      );

      const petToSave = {
        ...formData,
        images: imageUrls,
        createdAt: Date.now(),
        active: false, // Pending admin activation
        source: 'public_form'
      };
      
      // Remove termsAccepted from database record
      delete petToSave.termsAccepted;

      await set(newPetRef, petToSave);

      alert("¡Solicitud enviada con éxito! Un administrador revisará los datos pronto.");
      navigate('/');

    } catch (err) {
      console.error(err);
      setError("Error al enviar la solicitud. Por favor, intente de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (imagePreviews.length + files.length > 4) {
      alert('Solo se permiten un máximo de 4 imágenes');
      return;
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <form onSubmit={handleSubmit} className="bg-white shadow-2xl rounded-2xl px-8 pt-8 pb-8 mb-4 border border-blue-100">
        <h2 className="text-3xl font-bold text-center text-blue-600 mb-8">
          Formulario de Rescatista
        </h2>

        {/* Rescuer Section */}
        <div className="mb-8 p-6 bg-blue-50 rounded-xl">
          <h3 className="text-xl font-semibold text-blue-800 mb-4 border-b border-blue-200 pb-2">
            Datos del Rescatista
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Nombre Completo</label>
              <input
                type="text"
                name="rescuerName"
                value={formData.rescuerName}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Teléfono de Contacto</label>
              <input
                type="tel"
                name="rescuerPhone"
                value={formData.rescuerPhone}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
                placeholder="+51 ..."
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 text-sm font-bold mb-2">Correo Electrónico</label>
              <input
                type="email"
                name="rescuerEmail"
                value={formData.rescuerEmail}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              />
            </div>
          </div>
        </div>

        {/* Pet Section */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">
            Datos de la Mascota
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Nombre de la mascota</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Tipo</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              >
                <option value="">Seleccione</option>
                {Object.values(PET_TYPES).map(t => (
                  <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Color</label>
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Tamaño</label>
              <select
                name="size"
                value={formData.size}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              >
                <option value="">Seleccione</option>
                {Object.values(PET_SIZES).map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Sexo</label>
              <select
                name="sex"
                value={formData.sex}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              >
                <option value="">Seleccione</option>
                <option value="male">Macho</option>
                <option value="female">Hembra</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Edad (aprox)</label>
              <input
                type="text"
                name="age"
                value={formData.age}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
                placeholder="Ej: 2 meses, 1 año"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-gray-700 text-sm font-bold mb-2">Ubicación</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
                placeholder="Distrito, Ciudad"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-gray-700 text-sm font-bold mb-2">Descripción</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                rows="3"
                required
                placeholder="Cuenta un poco sobre la mascota y su historia..."
              ></textarea>
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="mb-8">
          <label className="block text-gray-700 text-sm font-bold mb-2">Imágenes (máximo 4)</label>
          <div className="flex flex-wrap gap-4 mb-4">
            {imagePreviews.map((preview, index) => (
              <div key={index} className="relative group">
                <img src={preview} alt="Preview" className="w-24 h-24 object-cover rounded-lg border" />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                >
                  ×
                </button>
              </div>
            ))}
            {imagePreviews.length < 4 && (
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-colors"
              >
                <span className="text-2xl">+</span>
                <span className="text-xs">Subir</span>
              </button>
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            onChange={handleImageChange}
            className="hidden"
          />
          <p className="text-xs text-gray-500 italic">Formatos permitidos: JPG, PNG. Máximo 4 fotos.</p>
        </div>

        {/* Terms and Conditions */}
        <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-bold text-gray-700 mb-2">Términos y Condiciones</h4>
          <div className="text-sm text-gray-600 h-32 overflow-y-auto mb-4 p-2 bg-white border rounded">
            <p className="mb-2">1. El rescatista declara que la información proporcionada es verídica.</p>
            <p className="mb-2">2. Salva Patas actúa únicamente como plataforma de difusión y no se hace responsable por el proceso de adopción directa entre el rescatista y el adoptante.</p>
            <p className="mb-2">3. El rescatista se compromete a informar si la mascota es adoptada por otros medios para dar de baja la publicación.</p>
            <p className="mb-2">4. Al subir las fotos, autoriza a Salva Patas a utilizarlas en sus redes sociales para la difusión de la mascota.</p>
            <p className="mb-2">5. Salva Patas se reserva el derecho de no publicar o dar de baja cualquier solicitud que no cumpla con los estándares de bienestar animal.</p>
          </div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              name="termsAccepted"
              checked={formData.termsAccepted}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              required
            />
            <span className="text-sm text-gray-700">Acepto los términos y condiciones</span>
          </label>
        </div>

        {error && <div className="mb-4 text-red-500 text-center font-semibold">{error}</div>}

        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-4 rounded-xl text-white font-bold text-lg shadow-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] ${
            isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600'
          }`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Enviando solicitud...
            </span>
          ) : 'Enviar Solicitud de Publicación'}
        </button>
      </form>
    </div>
  );
};

export default RescuerForm;
